import type Controller from '@ember/controller';
import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import config from 'garaje/config/environment';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type StateService from 'garaje/services/state';

interface AuditLogParams {
  page?: string | number;
  pageSize?: string | number;
  query?: string;
  sortBy?: string;
  sortDirection?: string;
  eventType?: string;
  stepType?: string;
  status?: string;
  locationIds?: string;
  startDate?: string;
  endDate?: string;
  integration?: string;
  exportId?: string;
}

export default class LocationOverviewHrisAuditLogRoute extends Route {
  @service declare ajaxFetch: AjaxFetchService;
  @service declare state: StateService;

  queryParams = {
    page: { refreshModel: true },
    pageSize: { refreshModel: true },
    query: { refreshModel: true },
    sortBy: { refreshModel: true },
    sortDirection: { refreshModel: true },
    eventType: { refreshModel: true },
    stepType: { refreshModel: true },
    status: { refreshModel: true },
    locationIds: { refreshModel: true },
    startDate: { refreshModel: true },
    endDate: { refreshModel: true },
    integration: { refreshModel: true },
    exportId: { refreshModel: false },
  };

  setupController(
    controller: Controller & { exportId?: string; downloadById: (id: string) => void },
    model: unknown,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    if (controller.exportId) {
      schedule('afterRender', this, () => controller.downloadById(controller.exportId!));
    }
  }

  async model(params: AuditLogParams): Promise<unknown> {
    const companyId = this.state.currentCompany?.id;

    const parts: string[] = [];
    if (companyId != null) parts.push(`company_id=${encodeURIComponent(String(companyId))}`);
    // API uses 0-indexed pages; UI uses 1-indexed
    const apiPage = Math.max(0, Number(params.page ?? 1) - 1);
    parts.push(`page=${encodeURIComponent(String(apiPage))}`);
    if (params.pageSize) parts.push(`page_size=${encodeURIComponent(String(params.pageSize))}`);
    if (params.query) parts.push(`query=${encodeURIComponent(params.query)}`);
    if (params.sortBy) parts.push(`sort_by=${encodeURIComponent(params.sortBy)}`);
    if (params.sortDirection) parts.push(`sort_direction=${encodeURIComponent(params.sortDirection)}`);
    if (params.eventType) parts.push(`event_type=${encodeURIComponent(params.eventType)}`);
    if (params.stepType) parts.push(`step_type=${encodeURIComponent(params.stepType)}`);
    if (params.status) parts.push(`status=${encodeURIComponent(params.status)}`);
    if (params.locationIds) parts.push(`location_ids=${encodeURIComponent(params.locationIds)}`);
    if (params.startDate) parts.push(`start_date=${encodeURIComponent(params.startDate)}`);
    if (params.endDate) parts.push(`end_date=${encodeURIComponent(params.endDate)}`);
    if (params.integration) parts.push(`integration=${encodeURIComponent(params.integration)}`);

    const qs = parts.join('&');
    const lookupsQs = companyId != null ? `?company_id=${encodeURIComponent(String(companyId))}` : '';

    // Explicit `hrisLifecycleApiHost` prefix — a relative URL resolves against
    // the current page origin (e.g. app.envoy.com in the location-view context)
    // rather than the dashboard host that Cloudflare routes to hris-lifecycle,
    // so the request 404s. Match the notifications + dashboard-config tabs.
    const host = config.hrisLifecycleApiHost || '';
    let auditData: Record<string, unknown> = {};
    let lookupsData: { integrations: string[]; locations: Array<{ id: number; name?: string }> } = {
      integrations: [],
      locations: [],
    };
    let unauthorized = false;

    try {
      [auditData, lookupsData] = (await Promise.all([
        this.ajaxFetch.request(`${host}/a/hris-lifecycle/api/v1/audit-events${qs ? `?${qs}` : ''}`),
        this.ajaxFetch.request(`${host}/a/hris-lifecycle/api/v1/audit-events/lookups${lookupsQs}`),
      ])) as [Record<string, unknown>, { integrations: string[]; locations: Array<{ id: number; name?: string }> }];
    } catch (err) {
      unauthorized = (err as { status?: number }).status === 403;
    }

    return {
      ...auditData,
      integrations: lookupsData.integrations,
      locations: lookupsData.locations,
      unauthorized,
    };
  }
}
